import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

export { UserLink, AstrologerLink, UserCell, AstrologerCell } from './EntityLink';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: number;
  changeLabel?: string;
  color?: string;
  loading?: boolean;
}

export function StatCard({ title, value, icon, change, changeLabel, color = 'text-primary-500', loading }: StatCardProps) {
  return (
    <div className="stat-card flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        {loading ? (
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse mt-2" />
        ) : (
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        )}
        {change !== undefined && !loading && (
          <p className={`flex items-center gap-1 text-xs mt-1.5 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(change)}% {changeLabel || 'vs last month'}
          </p>
        )}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-opacity-10 ${color.replace('text-', 'bg-')} bg-opacity-10`}>
        <span className={color}>{icon}</span>
      </div>
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
}
export function PageHeader({ title, subtitle, children }: PageHeaderProps) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

interface TableProps { children: React.ReactNode; }
export function Table({ children }: TableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">{children}</table>
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: cols }).map((__, j) => (
            <div key={j} className="flex-1 h-4 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      ))}
    </div>
  );
}

interface BadgeProps { status: string; }
export function StatusBadge({ status }: BadgeProps) {
  const map: Record<string, string> = {
    active: 'badge-green', approved: 'badge-green', online: 'badge-green', paid: 'badge-green', delivered: 'badge-green', completed: 'badge-green', ended: 'badge-green',
    pending: 'badge-yellow', waiting: 'badge-yellow', processing: 'badge-yellow', scheduled: 'badge-yellow',
    inactive: 'badge-gray', offline: 'badge-gray', cancelled: 'badge-gray', missed: 'badge-gray',
    failed: 'badge-red', rejected: 'badge-red', suspended: 'badge-red', blocked: 'badge-red', declined: 'badge-red',
    busy: 'badge-orange', attempted: 'badge-orange',
    replied: 'badge-blue', open: 'badge-orange', resolved: 'badge-green', created: 'badge-gray', refunded: 'badge-purple',
    placed: 'badge-purple',
    ready: 'badge-green', downloaded: 'badge-blue', free: 'badge-green', expired: 'badge-orange', none: 'badge-gray',
    generate: 'badge-blue', purchase: 'badge-purple', download: 'badge-blue', waived: 'badge-gray', archived: 'badge-gray',
  };
  return <span className={map[status?.toLowerCase()] || 'badge-gray'}>{status}</span>;
}

interface PaginationProps {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
}
export function Pagination({ page, pages, total, limit, onPageChange }: PaginationProps) {
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between text-sm text-gray-600 mt-4">
      <span>Showing {start}–{end} of {total}</span>
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">← Prev</button>
        {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
          const p = pages <= 7 ? i + 1 : i === 0 ? 1 : i === 6 ? pages : page - 3 + i;
          return (
            <button key={p} onClick={() => onPageChange(p)} className={`w-8 h-8 rounded border text-xs ${p === page ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 hover:bg-gray-50'}`}>{p}</button>
          );
        })}
        <button disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">Next →</button>
      </div>
    </div>
  );
}

export function EmptyState({ message = 'No records found', icon }: { message?: string; icon?: React.ReactNode }) {
  return (
    <div className="text-center py-16 text-gray-400">
      {icon && <div className="flex justify-center mb-3">{icon}</div>}
      <p className="text-sm">{message}</p>
    </div>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}
export function SearchInput({ value, onChange, placeholder = 'Search...' }: SearchInputProps) {
  return (
    <div className="relative">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input pl-9 w-full" />
    </div>
  );
}
